import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Edit, Trash2, Calendar } from 'lucide-react';
import { api } from '@/lib/api';
import type { MealSession, VenueArea } from '@/types';

export default function SessionsList() {
  const { venueId, areaId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: venue } = useQuery({
    queryKey: ['venue', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}`);
      return response.data;
    },
  });

  const { data: area } = useQuery<VenueArea>({
    queryKey: ['venue-area', venueId, areaId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/areas/${areaId}`);
      return response.data;
    },
  });

  const { data: sessions, isLoading } = useQuery<MealSession[]>({
    queryKey: ['area-sessions', venueId, areaId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/areas/${areaId}/sessions`);
      return response.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      await api.delete(`/admin/venues/${venueId}/areas/${areaId}/sessions/${sessionId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['area-sessions', venueId, areaId] });
      queryClient.invalidateQueries({ queryKey: ['venue-areas', venueId] });
    },
  });

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/venues/${venueId}/booking-config/areas?type=${area?.booking_type_id || ''}`)}
          className="p-2 hover:bg-gray-100 rounded-md"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Meal Sessions</h1>
          <p className="text-sm text-gray-500">
            {(venue as Record<string, unknown>)?.name as string} - {area?.name}
          </p>
        </div>
        <Link
          to={`/venues/${venueId}/booking-config/areas/${areaId}/sessions/new`}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Session
        </Link>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Session Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Gap Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Party Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                [...Array(3)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20 ml-auto"></div></td>
                  </tr>
                ))
              ) : sessions?.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    No sessions configured yet. Click "Add Session" to create one.
                  </td>
                </tr>
              ) : (
                sessions?.map((session) => (
                  <tr key={session.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{session.name}</p>
                        <p className="text-sm text-gray-500">{session.slug}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {formatDuration(session.default_duration_minutes)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">
                        {session.gap_between_bookings_minutes}m gap
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">
                        {session.min_party_size} - {session.max_party_size || '∞'} guests
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">
                        {session.booking_lead_time_hours}h advance
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        session.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {session.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Link
                          to={`/venues/${venueId}/booking-config/areas/${areaId}/sessions/${session.id}`}
                          className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                          title="Edit Session"
                        >
                          <Edit className="h-4 w-4" />
                        </Link>
                        <Link
                          to={`/venues/${venueId}/booking-config/areas/${areaId}/sessions/${session.id}/hours`}
                          className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                          title="Configure Hours"
                        >
                          <Calendar className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => {
                            if (confirm('Are you sure you want to delete this session?')) {
                              deleteMutation.mutate(session.id);
                            }
                          }}
                          className="p-2 rounded-md hover:bg-gray-100 text-red-500 hover:text-red-700"
                          title="Delete Session"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Info Card */}
      <div className="mt-6 card p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Session Settings Guide</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-medium text-gray-700">Duration</p>
            <p className="text-gray-500">How long each booking lasts by default (e.g., 90 minutes for dinner).</p>
          </div>
          <div>
            <p className="font-medium text-gray-700">Gap Time</p>
            <p className="text-gray-500">Buffer time between bookings for table turnover (e.g., 15 minutes).</p>
          </div>
          <div>
            <p className="font-medium text-gray-700">Party Size</p>
            <p className="text-gray-500">Minimum and maximum number of guests allowed per booking.</p>
          </div>
          <div>
            <p className="font-medium text-gray-700">Lead Time</p>
            <p className="text-gray-500">How far in advance bookings must be made (e.g., 24 hours).</p>
          </div>
        </div>
      </div>
    </div>
  );
}
